import { axiosGetV3 } from '../../api/axios-handler';
import { useQuery } from '@tanstack/react-query';
import { getSessionSeed } from '../../utils/sessionSeed';
import { Content } from './useExperiences';
import { useCrisisCheckOnSearchQuery } from './useCrisisCheck';

export interface ExperiencesResponse {
  count: number;
  data: Content[];
  page?: number;
  limit?: number;
  total_pages?: number;
  performance?: {
    algolia_search_ms: number;
    content_service_ms: number;
    total_ms: number;
  };
}

/** Landscape oEmbed size for MWA collection/browse tiles (portrait frames use object-cover). */
const COLLECTION_TILE_OEMBED = { width: 640, height: 360 } as const;

export type FetchVimeoMetadataOptions = {
  /** When set, requests a thumbnail at these dimensions from oEmbed. Omit for native video dimensions (Peersona intro). */
  oembedDimensions?: { width: number; height: number };
};

/**
 * Fetches Vimeo metadata (thumbnail URL, duration, and dimensions) for a video ID.
 * Supports unlisted videos via the optional privacy hash.
 */
export const fetchVimeoMetadata = async (
  videoId: string,
  privacyHash?: string,
  options?: FetchVimeoMetadataOptions,
): Promise<{
  thumbnailUrl: string | null;
  duration: number | null;
  width: number | null;
  height: number | null;
}> => {
  try {
    const pageUrl = privacyHash ? `https://vimeo.com/${videoId}?h=${privacyHash}` : `https://vimeo.com/${videoId}`;
    const dims = options?.oembedDimensions;
    const sizeParams = dims != null ? `&width=${dims.width}&height=${dims.height}` : '';
    const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(pageUrl)}${sizeParams}`;
    const response = await fetch(oembedUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch thumbnail/metadata');
    }
    const data = await response.json();
    return {
      thumbnailUrl: data.thumbnail_url || null,
      duration: data.duration || null,
      width: typeof data.width === 'number' ? data.width : null,
      height: typeof data.height === 'number' ? data.height : null,
    };
  } catch (error) {
    console.error('Error fetching Vimeo metadata:', error);
    return { thumbnailUrl: null, duration: null, width: null, height: null };
  }
};

/**
 * Fetches Vimeo metadata for multiple video IDs in parallel
 */
export const fetchVimeoMetadataBatch = async (
  videoIds: string[],
): Promise<Map<string, { thumbnailUrl: string | null; duration: number | null }>> => {
  const results = await Promise.allSettled(
    videoIds.map((videoId) =>
      fetchVimeoMetadata(videoId, undefined, { oembedDimensions: COLLECTION_TILE_OEMBED }).then((metadata) => ({
        videoId,
        ...metadata,
      })),
    ),
  );

  const metadataMap = new Map<string, { thumbnailUrl: string | null; duration: number | null }>();
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      metadataMap.set(result.value.videoId, {
        thumbnailUrl: result.value.thumbnailUrl,
        duration: result.value.duration,
      });
    } else {
      // On failure, set null values
      metadataMap.set(videoIds[index], { thumbnailUrl: null, duration: null });
    }
  });

  return metadataMap;
};

/**
 * Adds Vimeo metadata (thumbnails and durations) to a Content object if available
 * Shared utility for adding Vimeo metadata to Content objects
 */
export const addVimeoMetadataToExperiences = (
  content: Content,
  metadataMap: Map<string, { thumbnailUrl: string | null; duration: number | null }>,
): Content => {
  if (content.video_id && metadataMap.has(content.video_id)) {
    const metadata = metadataMap.get(content.video_id)!;
    return {
      ...content,
      // Convert null to undefined to match Content type
      vimeo_thumbnail_url: metadata.thumbnailUrl ?? undefined,
      duration: metadata.duration || content.duration,
    };
  }
  return content;
};

const fetchAndAddVimeoMetadataToExperiences = async (experiences: Content[]): Promise<Content[]> => {
  const videoExperiences = experiences.filter((exp: Content) => exp.video_id && !(exp as any).vimeo_thumbnail_url);

  if (videoExperiences.length === 0) {
    return experiences;
  }

  const videoIds = videoExperiences.map((exp: Content) => exp.video_id).filter(Boolean) as string[];
  const metadataMap = await fetchVimeoMetadataBatch(videoIds);

  return experiences.map((exp: Content) => addVimeoMetadataToExperiences(exp, metadataMap));
};

const buildCommonParams = (tagGroupId?: number, tagIds?: number[], experienceType?: string): Record<string, string> => {
  const params: Record<string, string> = {};

  if (tagGroupId) {
    params.tag_groups = tagGroupId.toString();
  }
  if (tagIds && tagIds.length > 0) {
    // Send tag_ids as comma-separated string to match backend DelimitedListField format
    params.tag_ids = tagIds.join(',');
  }
  if (experienceType) {
    params.experience_type = experienceType;
  }

  return params;
};

const getExperiences = async (
  tagGroupId: number | undefined,
  tagIds: number[] | undefined,
  experienceType: string | undefined,
  searchQuery: string | undefined,
  page: number,
  limit: number,
  listenerId?: number,
): Promise<ExperiencesResponse> => {
  // If there's a search query, use Algolia search endpoint
  if (searchQuery && searchQuery.trim().length > 0) {
    const params = {
      ...buildCommonParams(tagGroupId, tagIds, experienceType),
      q: searchQuery.trim(),
      source_type: 'content',
      seed: getSessionSeed(),
    };

    const response = await axiosGetV3('/simple_search/algolia/search', params);
    const data = response.data;

    data.data = await fetchAndAddVimeoMetadataToExperiences(data.data);

    return data;
  }

  // Otherwise use the browse endpoint
  const params: any = {
    ...buildCommonParams(tagGroupId, tagIds, experienceType),
    page,
    limit,
  };

  if (listenerId) {
    params.listener_role_ids = listenerId.toString();
  }
  params.seed = getSessionSeed();

  const response = await axiosGetV3('/listener_audio/browse', params);
  const data = response.data;

  data.data = await fetchAndAddVimeoMetadataToExperiences(data.data);

  return data;
};

export const useBrowseExperiences = (
  tagGroupId: number | undefined,
  clientId: number | undefined,
  tagIds: number[] | undefined,
  experienceType: string | undefined,
  searchQuery: string | undefined,
  page: number,
  limit: number,
  listenerId?: number,
) => {
  useCrisisCheckOnSearchQuery(searchQuery);

  // Note: clientId is kept for consistency with other hooks but not sent to the API
  // The listener_audio endpoint doesn't accept client_id parameter

  // Create a stable key for tagIds to prevent unnecessary refetches
  const tagIdsKey = tagIds && tagIds.length > 0 ? [...tagIds].sort((a, b) => a - b).join(',') : undefined;

  const queryKey = ['browseExperiences', tagGroupId, tagIdsKey, experienceType, searchQuery, page, limit, listenerId];

  const query = useQuery<ExperiencesResponse>(queryKey, () =>
    getExperiences(tagGroupId, tagIds, experienceType, searchQuery, page, limit, listenerId),
  );

  return query;
};
